import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';

import {
    PickerContext,
    PickerMode,
} from '@connect-services/client/components/modals/basemap-management-modal/basemap-management-modal.component';
import {
    BasemapInfo,
    BasemapLayerType,
    BasemapSecondary,
    TileSourceTypeEnum,
} from '@connect-services/client/services/basemap.service';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-basemap-management-modal-picker',
    styleUrl: './basemap-management-modal-picker.component.scss',
    templateUrl: './basemap-management-modal-picker.component.html',
})
export class BasemapManagementModalPickerComponent {
    public readonly allBasemapTypes = input.required<ReadonlyArray<TileSourceTypeEnum>>();
    public readonly catalog = input.required<Readonly<Record<TileSourceTypeEnum, BasemapInfo>>>();
    public readonly picker = input.required<PickerContext>();
    public readonly pickerLeft = input<number>(0);
    public readonly primaryBasemap = input.required<TileSourceTypeEnum>();
    public readonly secondariesBasemap = input.required<ReadonlyArray<BasemapSecondary>>();
    public readonly selectedPick = output<TileSourceTypeEnum>();

    protected readonly eligibleTypesRemaining = computed<TileSourceTypeEnum[]>(() => {
        const taken = new Set<TileSourceTypeEnum>([
            this.primaryBasemap(),
            ...this.secondariesBasemap().map((basemapSecondary: BasemapSecondary) => basemapSecondary.type),
        ]);

        const secondaryTypeToReplace = this.replacingSecondary();
        if (secondaryTypeToReplace) {
            taken.delete(secondaryTypeToReplace);
        }

        return this.allBasemapTypes().filter((tile: TileSourceTypeEnum) => !taken.has(tile));
    });
    protected readonly hoveredType = signal<TileSourceTypeEnum | null>(null);
    protected readonly PickerMode = PickerMode;
    protected readonly pickerPositionClass = computed(() =>
        this.picker()?.mode === PickerMode.PRIMARY ? 'left-[var(--picker-left)] translate-x-0' : 'left-[calc(79%)]',
    );
    protected readonly pickerTypes = computed<ReadonlyArray<TileSourceTypeEnum>>(() => {
        const picker = this.picker();
        if (!picker) {
            return [];
        }

        return picker.mode === PickerMode.PRIMARY ? this.allBasemapTypes() : this.eligibleTypesRemaining();
    });
    protected readonly replacingSecondary = computed<TileSourceTypeEnum | null>(() => {
        const picker = this.picker();

        return picker?.mode === PickerMode.SECONDARY_REPLACE ? picker.secondaryTypeToReplace : null;
    });

    protected isBackground(type: TileSourceTypeEnum): boolean {
        return this.catalog()[type].layerType === BasemapLayerType.BACKGROUND;
    }

    protected onPick(type: TileSourceTypeEnum): void {
        this.selectedPick.emit(type);
    }
}
